import useHotkey from '../hooks/use-hotkey';
import { setHotkey } from '@form-engine/builder-application/keybindings';
import { useFormContext } from '@form-engine/core-application/hooks/use-form';
import { useCallback } from 'react';
import { FormDefinition } from '@form-engine/core-application/types/form';
import FormEngine from '@form-engine/core-application';
import Builder from '@form-engine/builder-application/index';
import saveFile from '@form-engine/builder-application/utilities/save-file';
import z from 'zod';

export const FormL10nSchema = z.record(
    z
        .object({
            elementName: z.string(),
            l10n: z.record(z.record(z.any())),
        })
        .strict()
);

export type FormL10n = z.infer<typeof FormL10nSchema>;

export function getFormL10nData(source: FormDefinition) {
    const l10n: FormL10n = {};

    for (const element of source.elements ?? []) {
        const translateableFields = Object.entries(
            Builder.maskConfigurations[element.type]?.translate?.permissions ??
                {}
        )
            .filter(([, value]) => value === 'write')
            .map(([key]) => key as keyof typeof element);

        if (!translateableFields.length) {
            continue;
        }

        const elementL10n: FormL10n[string] = (l10n[element.id] = {
            elementName: element.name ?? '',
            l10n: {},
        });

        for (const key of translateableFields) {
            elementL10n.l10n[key] = {
                default: element[key],
            };

            for (const [locale, localeData] of Object.entries(
                element.locales ?? {}
            )) {
                if (key in localeData) {
                    elementL10n.l10n[key][locale] = localeData[key];
                }
            }
        }
    }

    return l10n;
}

function exportFormAsL10nJson(
    source: FormDefinition,
    filename = 'export.l10n.json',
    type = 'application/json'
) {
    saveFile(
        JSON.stringify(getFormL10nData(source), undefined, 2),
        filename,
        type
    );
}

export default function useExportL10nAction() {
    const { state } = useFormContext();
    return useCallback(async () => {
        exportFormAsL10nJson(
            { ...state, engineVersion: FormEngine.version },
            `${state.name || 'untitled'}.l10n.json`
        );
    }, [state]);
}

export const ACTION_NAME = 'formEditor.form.exportL10n';

setHotkey(ACTION_NAME, 'ctrl+alt+l,meta+alt+l');

export const useAction = useExportL10nAction;

export function useActionHotkey() {
    useHotkey(ACTION_NAME, useAction());
}
